import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'

import type { CoachMessage } from '../../../../../../pure-js/types/CoachTypes'
import { delay, isCallableAnswerLost } from '../../../../../../pure-js/libs/Common'
import { ONE_SECOND } from '../../../../../../pure-js/libs/Consts'
import { sendErrorToSlack } from '../../../../libs/SlackHelper'
import { coachConversationQuery, refreshGoalsAfterTurn } from '../../../../apiLayer/queryHooks/coach'
import { findTurnOfSentMessage, type SentCoachMessage } from './coachTranscript'

const RECHECK_INTERVAL_MS = 3 * ONE_SECOND // 3 s between reads while her reply is still being written
const REPLY_WAIT_MS = 150 * ONE_SECOND // 2.5 min from sending, longer than any reply takes

type StoredTurn = ReturnType<typeof findTurnOfSentMessage>

/** Reads the stored thread, and again while her reply to the sent message may still be on its way. */
async function readTurnUntilAnswered(params: {
  queryClient: QueryClient
  sent: SentCoachMessage
  isPageOpen: () => boolean
}): Promise<StoredTurn> {
  const { queryClient, sent, isPageOpen } = params
  const thread = await queryClient.fetchQuery(coachConversationQuery(sent.conversationId))
  const turn = findTurnOfSentMessage(thread.messages, sent.clientMessageId)

  const isReplyOnItsWay = turn.isStored && !turn.reply
  const canWaitLonger = isPageOpen() && Date.now() < sent.sentAt + REPLY_WAIT_MS
  if (!isReplyOnItsWay || !canWaitLonger) return turn

  await delay(RECHECK_INTERVAL_MS)
  return readTurnUntilAnswered(params)
}

/** A send can lose its answer after the message was stored and answered, as when a phone puts the page to sleep. */
export function useLostAnswerRecovery(): (error: unknown, sent: SentCoachMessage) => Promise<CoachMessage | undefined> {
  const queryClient = useQueryClient()
  const isMountedRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  return useCallback(
    async (error: unknown, sent: SentCoachMessage) => {
      if (!isCallableAnswerLost(error)) return undefined

      try {
        const turn = await readTurnUntilAnswered({ queryClient, sent, isPageOpen: () => isMountedRef.current })
        // The turn may have saved or retired a goal, which its own answer would have refreshed.
        if (turn.reply) refreshGoalsAfterTurn(queryClient)

        // Stored, waited for in full and still unanswered: her reply was lost, which is a fault worth knowing about.
        const isReplyLost = turn.isStored && !turn.reply && isMountedRef.current
        if (isReplyLost)
          void sendErrorToSlack(new Error('coach-reply-never-stored'), undefined, 'useLostAnswerRecovery')

        return turn.reply
      } catch {
        // A thread that cannot be read is no sign the message arrived.
        return undefined
      }
    },
    [queryClient]
  )
}
